import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { NavItem, NavButton } from './components';
import SearchField from 'components/SearchField';
import AkygaLogo from 'static/akyga.png';
import AkygaDarkLogo from 'static/akyga.png';
import { useTranslation } from 'react-i18next';
import { getBasketFromLocalStorage } from '@akyga/shared-ui';
import { useNavigate } from 'react-router-dom';
import { NavigationPages } from 'layouts/Main/navigation';

interface TopbarProps {
  onSidebarOpen: () => void;
  pages: NavigationPages;
  colorInvert?: boolean;
}

const Topbar = ({ onSidebarOpen, pages, colorInvert = false }: TopbarProps) => {
  const theme = useTheme();
  const { mode } = theme.palette;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { main: mainPages } = pages;

  const [basket, setBasket] = useState(getBasketFromLocalStorage());
  const [highlight, setHighlight] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const handleBasketChange = () => {
      setBasket(getBasketFromLocalStorage());
      setHighlight(true);
      setTimeout(() => setHighlight(false), 300);
    };

    window.addEventListener('basketChange', handleBasketChange);
    return () => window.removeEventListener('basketChange', handleBasketChange);
  }, []);

  const itemCount = Object.keys(basket).length;
  return (
    <Box
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
      width={1}
    >
      <Box
        display={'flex'}
        component="a"
        href="/"
        title="Akyga"
        width={{ xs: 100, md: 130 }}
      >
        <Box
          component={'img'}
          src={mode === 'light' && !colorInvert ? AkygaLogo : AkygaDarkLogo}
          height={1}
          width={1}
        />
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'flex' } }} alignItems={'center'}>
        {mainPages.map((page, index) =>
          page.items ? (
            <Box key={index} marginLeft={4}>
              <NavItem
                title={t(page.title)}
                id={page.id}
                items={page.items}
                colorInvert={colorInvert}
              />
            </Box>
          ) : (
            <Box key={index} marginLeft={4}>
              <NavButton
                title={t(page.title)}
                id={page.id}
                url={page.href ?? '/'}
                colorInvert={colorInvert}
              />
            </Box>
          ),
        )}
        <Box marginLeft={4}>
          {searchOpen ? (
            <SearchField
              autoFocus
              label={'Search batteries & power sources'}
              sx={{ width: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              onBlur={(event) => {
                if (!event.target.value.trim()) setSearchOpen(false);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setSearchOpen(false);
              }}
            />
          ) : (
            <IconButton
              onClick={() => setSearchOpen(true)}
              aria-label={t('topbar.search')}
              sx={{
                color: colorInvert
                  ? theme.palette.background.paper
                  : theme.palette.primary.main,
              }}
            >
              <SearchIcon />
            </IconButton>
          )}
        </Box>
        <Box marginLeft={4} sx={{ position: 'relative' }}>
          <Badge badgeContent={itemCount} color="error">
            <Button
              variant="contained"
              color="primary"
              component="a"
              target="blank"
              onClick={() => navigate('/basket')}
              size="medium"
              sx={{
                animation: highlight ? 'pulse 0.6s' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                  '100%': { transform: 'scale(1)' },
                },
              }}
            >
              {t('topbar.getQuote')}
            </Button>
          </Badge>
        </Box>
      </Box>
      <Box
        sx={{ display: { xs: 'flex', md: 'none' } }}
        alignItems={'center'}
        gap={1}
      >
        <Button
          onClick={() => navigate('/search')}
          aria-label={t('topbar.search')}
          variant={'outlined'}
          sx={{
            borderRadius: 2,
            minWidth: 'auto',
            padding: 1,
            borderColor: alpha(theme.palette.divider, 0.2),
            color: colorInvert
              ? theme.palette.background.paper
              : theme.palette.primary.main,
          }}
        >
          <SearchIcon />
        </Button>
        <Badge badgeContent={itemCount} color="error">
          <Button
            onClick={() => onSidebarOpen()}
            aria-label={t('topbar.menu')}
            variant={'outlined'}
            sx={{
              borderRadius: 2,
              minWidth: 'auto',
              padding: 1,
              borderColor: alpha(theme.palette.divider, 0.2),
              color: colorInvert
                ? theme.palette.background.paper
                : theme.palette.primary.main,
            }}
          >
            <MenuIcon />
          </Button>
        </Badge>
      </Box>
    </Box>
  );
};

export default Topbar;
